import { useContext, useEffect, useState } from 'react';
import { useNotificacao } from '../../../../../../hooks/useNotificacao';
import { IMunicipio } from '../../../../../../interfaces/IProcessos';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { useQuery } from '@tanstack/react-query';
import { useBackEndRequests } from '../../../../lib/backEndRequests';
import { validarDocumento } from '../../../../../../utils/validarDocumento';
import classNames from 'classnames';
import style from './primeiroAdquirenteFiliais.module.scss';
import { Col, Row } from 'react-dsgov';
import { BrButton, BrDivider, BrInput, BrSelect } from '@govbr-ds/react-components';
import { formatarCNPJOrCpf, unmaskSelectCpfCnpj } from '../../../../../../utils/formatCNPJOrCPF';
import { estadosUFOptions } from '../../../../../../utils/estadosUFLista';
import { DiefCfemContext } from '../../../../../../contexts/DiefCfemContext';
import { IFilialLista } from '../../../../../../interfaces/IIdentificacaoBasica';
import { ETipoDeclarante } from 'enums/ETipoDeclarante';

const PrimeiroAdquirenteFiliais = ({ filiaisSalvas }: { filiaisSalvas?: IFilialLista[] }) => {
  const [estadoUF, setEstadoUF] = useState<string>('');
  const [municipio, setMunicipio] = useState<{
    nome: string;
    codigoIBGE: string;
  }>({
    nome: '',
    codigoIBGE: '',
  });
  const [cnpjFilial, setCnpjFilial] = useState<string>('');
  const [razaoSocial, setRazaoSocial] = useState<string>('');

  const [municipios, setMunicipios] = useState<IMunicipio[]>([]);

  const mostrarNotificacao = useNotificacao();

  const { getMunicipioPorUf, getListaFiliaisDeclaracaoAnterior } = useBackEndRequests();

  const context = useContext(DiefCfemContext);

  const declaracaoPrimeiroAdquirente = context?.declaracaoPrimeiroAdquirente;

  const filiaisLista = filiaisSalvas ? filiaisSalvas : declaracaoPrimeiroAdquirente?.filiais;

  const { data: municipiosPorUfData } = useQuery<IMunicipio[], IErrorWithSentryId>({
    queryKey: ['municipioPorUfQuery', estadoUF],
    queryFn: () => getMunicipioPorUf(estadoUF),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: !!estadoUF,
  });

  const cpfCnpj = context?.declaracaoPrimeiroAdquirente?.declarante?.cpfCnpj || '';

  const { data: filiaisDeclaracaoAnterior } = useQuery<IFilialLista[], IErrorWithSentryId>({
    queryKey: ['getListaFiliaisDeclaracaoAnteriorQuery', cpfCnpj, ETipoDeclarante.PRIMEIRO_ADQUIRENTE],
    queryFn: () => getListaFiliaisDeclaracaoAnterior(cpfCnpj, ETipoDeclarante.PRIMEIRO_ADQUIRENTE),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: !!cpfCnpj,
  });

  useEffect(() => {
    if (filiaisDeclaracaoAnterior && filiaisDeclaracaoAnterior.length > 0 && !filiaisSalvas) {
      context?.setDeclaracaoPrimeiroAdquirente((prev) => {
        if (prev.filiais && prev.filiais.length > 0) {
          return prev;
        }
        return {
          ...prev,
          filiais: filiaisDeclaracaoAnterior,
        };
      });
    }
  }, [filiaisDeclaracaoAnterior]);

  useEffect(() => {
    if (municipiosPorUfData) {
      setMunicipios(municipiosPorUfData);
    }
  }, [municipios, municipiosPorUfData]);

  const enviarFilial = async () => {
    if (!validarDocumento(cnpjFilial)) {
      mostrarNotificacao({
        conteudo: 'CNPJ inválido.',
        tipo: 'danger',
      });
      return;
    }
    if (!razaoSocial) {
      mostrarNotificacao({
        conteudo: 'Razão Social é obrigatório.',
        tipo: 'danger',
      });
      return;
    }
    if (!estadoUF) {
      mostrarNotificacao({
        conteudo: 'UF é obrigatório.',
        tipo: 'danger',
      });
      return;
    }
    if (!municipio.codigoIBGE) {
      mostrarNotificacao({
        conteudo: 'Município é obrigatório.',
        tipo: 'danger',
      });
      return;
    }
    if (declaracaoPrimeiroAdquirente?.filiais?.find((filial) => filial.cnpj === cnpjFilial.replace(/[./-]/g, ''))) {
      mostrarNotificacao({
        conteudo: 'CNPJ já listado.',
        tipo: 'danger',
      });
      return;
    }
    if (razaoSocial && municipio.codigoIBGE) {
      context?.setDeclaracaoPrimeiroAdquirente({
        ...context.declaracaoPrimeiroAdquirente,
        filiais: [
          ...(context.declaracaoPrimeiroAdquirente?.filiais ?? []),
          {
            cnpj: cnpjFilial.replace(/[./-]/g, ''),
            nomeRazaoSocial: razaoSocial,
            municipio: {
              nome: municipio.nome,
              codigoIBGE: municipio.codigoIBGE,
              uf: estadoUF,
            },
          },
        ],
      });
      setCnpjFilial('');
      setRazaoSocial('');
      setEstadoUF('');
      setMunicipio({ nome: '', codigoIBGE: '' });
    }
  };

  const deletarFilial = async (idFilial: string) => {
    context?.setDeclaracaoPrimeiroAdquirente({
      ...context.declaracaoPrimeiroAdquirente,
      filiais: context.declaracaoPrimeiroAdquirente?.filiais?.filter((filial) => filial.cnpj !== idFilial) || [],
    });
  };

  return (
    <div className={classNames(style.container, 'PrimeiroAdquirenteFiliais')}>
      <h4 className={style.title}>Filial/Representante Comercial</h4>
      {!filiaisSalvas && (
        <div className={style.form}>
          <Row className={style.row}>
            <Col size={3}>
              <span className={style.label}>CNPJ</span>
              <BrInput
                data-testid='input-cnpj'
                id='input-cnpj'
                value={formatarCNPJOrCpf(cnpjFilial)}
                onChange={(e) => setCnpjFilial(unmaskSelectCpfCnpj(e.target.value).slice(0, 14))}
              />
            </Col>
            <Col size={3}>
              <span className={style.label}>Razão Social</span>

              <BrInput
                data-testid='input-razao'
                id='input-razao'
                maxLength={200}
                value={razaoSocial}
                onChange={(e) => setRazaoSocial(e.target.value)}
              />
            </Col>
            <Col size={2}>
              <span className={style.label}>UF</span>
              <BrSelect
                data-testid='select-uf'
                id='select-uf'
                key={estadoUF}
                className={style.select}
                options={estadosUFOptions}
                value={estadoUF}
                onChange={(value: string) => {
                  setEstadoUF(value);
                  setMunicipio({ nome: '', codigoIBGE: '' });
                }}
              />
            </Col>
            <Col size={3}>
              <span className={style.label}>Município</span>
              <BrSelect
                data-testid='select-municipio'
                id='select-municipio'
                key={municipio.codigoIBGE}
                className={style.select}
                options={municipios.map((municipio) => ({
                  label: municipio.nome,
                  value: municipio.codigoIBGE.toString(),
                }))}
                disabled={!estadoUF}
                value={municipio.codigoIBGE.toString()}
                onChange={(value: string) => {
                  const selectedMunicipio = municipios.find((m) => m.codigoIBGE.toString() === value);
                  if (selectedMunicipio) {
                    setMunicipio(selectedMunicipio);
                  }
                }}
              />
            </Col>
            <Col size={1} className={style.buttonContainer}>
              <BrButton
                data-testid='button-enviar-filial'
                id='button-enviar-filial'
                circle
                icon='fas fa-plus'
                secondary
                onClick={() => enviarFilial()}
              />
            </Col>
          </Row>
        </div>
      )}
      <div className={style.lista}>
        <div className={style.cabecalho}>
          <Row className={style.row}>
            <Col size={3}>
              <span className={style.cabecalhoLabel}>CNPJ</span>
            </Col>
            <Col size={3}>
              <span className={style.cabecalhoLabel}>Razão Social</span>
            </Col>
            <Col size={2}>
              <span className={style.cabecalhoLabel}>UF</span>
            </Col>
            <Col size={3}>
              <span className={style.cabecalhoLabel}>Municipio</span>
            </Col>
            {!filiaisSalvas && (
              <Col size={1} className={style.acoesColumn}>
                <span className={style.cabecalhoLabel}>Ações</span>
              </Col>
            )}
          </Row>
        </div>
        {filiaisLista?.length === 0 && (
          <div className={style.emptyText}>Não há Filial/Representante Comercial adicionados</div>
        )}
        {filiaisLista?.map((filial) => (
          <div key={filial.cnpj} className={style.linhaFilial}>
            <Row className={style.row}>
              <Col size={3} className={style.infoColumn}>
                {formatarCNPJOrCpf(filial.cnpj)}
              </Col>
              <Col size={3} className={style.infoColumn}>
                {filial.nomeRazaoSocial}
              </Col>
              <Col size={2} className={style.infoColumn}>
                {filial.municipio.uf}
              </Col>
              <Col size={3} className={style.infoColumn}>
                {filial.municipio.nome}
              </Col>
              {!filiaisSalvas && (
                <Col size={1} className={style.acoesColumn}>
                  <BrButton
                    data-testid={`button-deletar-${filial.cnpj}`}
                    size='small'
                    circle
                    icon='fas fa-trash'
                    onClick={() => deletarFilial(filial?.cnpj)}
                  />
                </Col>
              )}
            </Row>
            <BrDivider />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PrimeiroAdquirenteFiliais;
